import React from "react";
import { Link } from 'react-router-dom';
import { ArrowRight, User, CheckCircle } from 'lucide-react';

// --- Reusable Sub-Components ---

const AgentCard = ({ name, email }) => (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
        <div className="w-full h-64 sm:h-72 lg:h-80 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            <User className="w-20 h-20 sm:w-24 sm:h-24 text-gray-400 relative z-10" />
        </div>
        <div className="p-4 sm:p-6 flex items-center justify-between">
            <div className="flex-1 min-w-0">
                <h3 className="font-bold text-base sm:text-lg text-gray-900 truncate">{name}</h3>
                <p className="text-xs sm:text-sm text-gray-600 truncate">{email}</p>
            </div>
            <Link 
                to="/agent" 
                className="ml-3 text-gray-500 hover:text-orange-500 transition-colors flex-shrink-0"
            >
                <ArrowRight className="w-6 h-6 sm:w-7 sm:h-7" />
            </Link>
        </div>
    </div>
);

const AmenityBullet = ({ text }) => (
    <li className="flex items-start mb-3">
        <CheckCircle className="w-5 h-5 text-orange-400 mr-3 flex-shrink-0 mt-0.5" />
        <span className="text-white text-sm sm:text-base">{text}</span>
    </li>
);

const AmenityItem = ({ number, title, features }) => (
    <div className="mb-6 sm:mb-8 p-5 sm:p-6 bg-orange-600/30 rounded-xl backdrop-blur-sm shadow-lg border border-orange-500/50 hover:bg-orange-600/40 transition-all">
        <div className="flex items-baseline mb-4">
            <span className="text-2xl sm:text-3xl font-bold text-white mr-3">{number}.</span>
            <h3 className="text-lg sm:text-xl font-bold text-white">{title}</h3>
        </div>
        <ul className="list-none pl-0 space-y-2">
            {features.map((feature, index) => (
                <AmenityBullet key={index} text={feature} />
            ))}
        </ul>
    </div>
);

// --- Property Card Component ---
const PropertyCard = ({ category, status, location, name, beds, baths, sqft, agentName, price }) => (
    <Link 
        to="/rent" 
        className="block bg-white border border-gray-200 rounded-xl overflow-hidden group hover:shadow-xl transition-all duration-300"
    >
        <div className="relative">
            <div className="w-full h-48 sm:h-56 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
            </div>
            <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                    category === 'Residential' ? 'bg-green-500 text-white' : 
                    category === 'Commercial' ? 'bg-blue-500 text-white' :
                    'bg-orange-500 text-white'
                }`}>
                    {category}
                </span>
                <span className="bg-gray-900 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {status}
                </span>
            </div>
        </div>
        <div className="p-4">
            <div className="flex items-center text-gray-500 text-sm mb-2">
                <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <span className="truncate">{location}</span>
            </div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate mb-3">{name}</h3>
            {beds > 0 && (
                <div className="flex items-center text-gray-600 text-sm space-x-3 sm:space-x-4 border-t pt-3">
                    <span className="whitespace-nowrap">Beds: <strong>{beds}</strong></span>
                    <span className="whitespace-nowrap">Baths: <strong>{baths}</strong></span>
                    <span className="whitespace-nowrap">Sqft: <strong>{sqft}</strong></span>
                </div>
            )}
        </div>
        <div className="px-4 pb-4 flex items-center justify-between border-t pt-4">
            <div className="flex items-center min-w-0 flex-1 mr-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex-shrink-0 mr-2"></div>
                <span className="text-sm text-gray-700 truncate">{agentName}</span>
            </div>
            <span className="font-bold text-gray-900 whitespace-nowrap">ZAR {price}</span>
        </div>
    </Link>
);

const PropertiesSection = () => {
    const properties = [
        { category: "Commercial", status: "Rent", location: "Johannesburg", name: "RPA Centre", beds: 0, baths: 0, sqft: 0, agentName: "Steve Parker", price: "25,000" },
        { category: "Residential", status: "Rent", location: "Pretoria", name: "Modern Apartment", beds: 2, baths: 1, sqft: 850, agentName: "Sarah Miller", price: "12,500" },
        { category: "Industrial", status: "Rent", location: "Benoni", name: "Warehouse Space", beds: 0, baths: 0, sqft: 5000, agentName: "John Doe", price: "45,000" },
        { category: "Residential", status: "Rent", location: "Sandton", name: "Luxury Villa", beds: 4, baths: 3, sqft: 2200, agentName: "Emily White", price: "35,000" },
        { category: "Commercial", status: "Rent", location: "Rosebank", name: "Office Suite", beds: 0, baths: 0, sqft: 1200, agentName: "Steve Parker", price: "28,000" },
        { category: "Residential", status: "Rent", location: "Johannesburg", name: "Hinton House", beds: 3, baths: 2, sqft: 1500, agentName: "Sarah Miller", price: "18,500" },
    ];

    return (
        <section className="bg-gray-50 py-12 sm:py-16 lg:py-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                    <span className="text-xs sm:text-sm text-gray-600 uppercase tracking-wider">LATEST PROPERTIES</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-black leading-tight mb-6 sm:mb-8">
                    Explore Our Properties
                </h2>

                <div className="flex items-center gap-2 mb-8 sm:mb-12 overflow-x-auto pb-2 hide-scrollbar">
                    <button className="px-4 py-2 text-sm font-semibold text-white bg-black rounded-full shadow-sm whitespace-nowrap">
                        View All
                    </button>
                    <button className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-100 whitespace-nowrap">
                        Industrial
                    </button>
                    <button className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-100 whitespace-nowrap">
                        Commercial
                    </button>
                    <button className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-100 whitespace-nowrap">
                        Residential
                    </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
                    {properties.map((prop, index) => (
                        <PropertyCard key={index} {...prop} />
                    ))}
                </div>

                <div className="text-center">
                    <Link
                        to="/properties" 
                        className="inline-block px-6 sm:px-8 py-3 text-sm font-bold text-white bg-black rounded-full hover:bg-gray-800 transition-colors duration-300"
                    >
                        View all properties
                    </Link>
                </div>
            </div>
        </section>
    );
};

// --- Main Requirements Component ---

const Requirements = () => {
    return (
        <div className="min-h-screen bg-white pt-16 sm:pt-20">
            {/* Meet the Experts Section */}
            <section className="bg-white py-12 sm:py-16 lg:py-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center mb-4">
                        <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                        <span className="text-xs sm:text-sm text-gray-600 uppercase tracking-wider">OUR AGENTS</span>
                    </div>
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12 sm:mb-16 gap-6">
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black leading-tight lg:w-1/2">
                            Meet the experts behind your real estate journey
                        </h2>
                        <p className="text-gray-700 text-base sm:text-lg leading-relaxed lg:w-1/2 lg:pl-8 xl:pl-16">
                            Our agents combine local knowledge, market expertise, and a passion for helping clients find the right property. We're committed to making your experience seamless and successful.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                        <AgentCard name="Steve Parker" email="steveparker@example.com" />
                        <AgentCard name="Sarah Miller" email="sarah.m@example.com" />
                        <AgentCard name="John Doe" email="john.d@example.com" />
                        <AgentCard name="Emily White" email="emily.w@example.com" />
                    </div>
                </div>
            </section>

            {/* Amenities Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 bg-cover bg-center relative" 
                style={{ backgroundImage: "url('https://images.unsplash.com/photo-1543888741-94576395561a?q=80&w=1770&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')" }}
            >
                <div className="absolute inset-0 bg-black/70"></div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start">
                        <div className="lg:w-1/2 flex flex-col justify-center">
                            <div className="flex items-center mb-4">
                                <div className="w-3 h-3 bg-white rounded-full mr-3"></div>
                                <span className="text-xs sm:text-sm text-white uppercase tracking-wider">ALL-INCLUSIVE</span>
                            </div>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6 sm:mb-8">
                                Amenities you can rely on
                            </h2>
                            <p className="text-white text-base sm:text-lg leading-relaxed mb-8 sm:mb-10">
                                Our properties are designed with convenience and functionality in mind. Each rental category comes with amenities tailored to meet your needs.
                            </p>
                            
                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                <img 
                                    src="https://images.unsplash.com/photo-1631510390389-c1e4fb20ff31?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=2184" 
                                    alt="Modern interior design" 
                                    className="rounded-lg shadow-lg w-full h-40 sm:h-48 object-cover"
                                />
                                <img 
                                    src="https://images.unsplash.com/photo-1613977257363-707ba9348227?q=80&w=1770&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
                                    alt="Office building exterior" 
                                    className="rounded-lg shadow-lg w-full h-40 sm:h-48 object-cover"
                                />
                                <img 
                                    src="https://images.unsplash.com/photo-1749244768351-2726dc23d26c?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=2224" 
                                    alt="Warehouse interior" 
                                    className="rounded-lg shadow-lg w-full h-40 sm:h-48 object-cover"
                                />
                                <img 
                                    src="https://images.unsplash.com/photo-1698047736474-3c1e5a0d3526?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=2128" 
                                    alt="Another modern interior" 
                                    className="rounded-lg shadow-lg w-full h-40 sm:h-48 object-cover"
                                />
                            </div>
                        </div>
                        
                        <div className="lg:w-1/2 mt-8 lg:mt-0">
                            <AmenityItem 
                                number="01" 
                                title="Residential" 
                                features={[
                                    "Secure parking",
                                    "Prepaid electricity & water",
                                    "Fibre/Wi-Fi ready",
                                    "Built-in cupboards & modern finishes",
                                    "24/7 Security"
                                ]} 
                            />
                            <AmenityItem 
                                number="02" 
                                title="Commercial" 
                                features={[
                                    "Reception & shared facilities",
                                    "High-speed internet access",
                                    "Secure tenant & visitor parking",
                                    "Call centre & other office services",
                                    "Access control systems"
                                ]} 
                            />
                            <AmenityItem 
                                number="03" 
                                title="Industrial" 
                                features={[
                                    "Large loading bays & truck access",
                                    "3-Phase power supply",
                                    "High ceilings for storage/operations",
                                    "24/7 security presence",
                                    "Easy access to major transport routes"
                                ]} 
                            />
                        </div>
                    </div>
                </div>
            </section>

            <PropertiesSection />

            <style jsx>{`
                .hide-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .hide-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

        </div>
    );
};

export default Requirements;